import ListMenuItem from "@/app/components/menu/listMenuItem";
import { OverlayMenuBaseProps } from "@/app/components/menu/overlayMenuSystem";
import style from "@/app/components/menu/overlayMenuSystem.module.scss";
import AccountMenuReview from "@/app/layouts/standard/header/accountMenuReview";
import Button from "@/components/design-system/button";
import { resetApiState } from "@/store/baseApi";
import { useAppDispatch } from "@/store/hooks";
import type { UserState } from "@/store/userApi";
import { removeCityAndZipcodeCookies } from "@/utils/locationDetails";
import { logout } from "@net32/library-core/fetch";
import BoxSVG from "public/next-public/common/icons/box-icon.svg";
import HeartSVG from "public/next-public/common/icons/heart-icon.svg";
import ListSVG from "public/next-public/common/icons/list-icon.svg";
import ReorderSVG from "public/next-public/common/icons/reorder-icon.svg";
import RepeatSVG from "public/next-public/common/icons/repeat-icon.svg";
import StarSVG from "public/next-public/common/icons/star-icon.svg";
import UserSVG from "public/next-public/common/icons/user-icon.svg";
import { useCallback } from "react";

export default function AccountMenuV2({
  navigateToSubMenuCallback,
  closeCallback,
  user,
  createAccountURL,
  loginURL,
  isPdpMvpEnabled,
}: AccountMenuV2Props) {
  const dispatch = useAppDispatch();
  const isGuest = !user || user.IsGuestUser === true;

  const logoutCallback = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      try {
        await logout();
        removeCityAndZipcodeCookies();
        dispatch(resetApiState());
        window.location.href = "/";
      } catch (error) {
        console.error("Logout error", error);
        alert("There was an error logging you out.  Please try again shortly.");
      }
    },
    [dispatch],
  );

  return (
    <div className={style.overlayMenuContainer}>
      {isGuest && (
        <div className={style.signInContainer}>
          <Button
            className={style.logOutButton}
            variant="primary"
            color="blue"
            text="Sign In"
            showLeftIcon={false}
            showRightIcon={false}
            link={{ href: loginURL }}
          />
          <Button
            className={style.createAccountButton}
            variant="secondary"
            color="blue"
            text="Create Account"
            showLeftIcon={false}
            showRightIcon={false}
            link={{ href: createAccountURL }}
          />
        </div>
      )}
      <div className={style.menuListItemContainer}>
        <ListMenuItem linkTo="/account" id="myaccount-menu-link" iconDecorationPrefix={<UserSVG />}>
          My Account
        </ListMenuItem>
        <ListMenuItem linkTo="/account/orders" id="order-history-menu-link" iconDecorationPrefix={<BoxSVG />}>
          Order History
        </ListMenuItem>
        <ListMenuItem linkTo="/reorder" id="reorder-menu-link" iconDecorationPrefix={<ReorderSVG />}>
          Reorder
        </ListMenuItem>
        <ListMenuItem
          linkTo="/account/orders/auto-order"
          id="auto-order-menu-link"
          iconDecorationPrefix={<RepeatSVG />}
        >
          Auto Order
        </ListMenuItem>
        <ListMenuItem
          linkTo="/shopping-list"
          id="shopping-list-menu-link"
          iconDecorationPrefix={isPdpMvpEnabled ? <HeartSVG /> : <ListSVG />}
        >
          Shopping List
        </ListMenuItem>
        <ListMenuItem
          id="review-product-menu-link"
          subMenu={<AccountMenuReview headerText={"Leave a Review"} />}
          navigateToSubMenuCallback={navigateToSubMenuCallback}
          iconDecorationPrefix={<StarSVG />}
          descriptionSuffix="Win an iPad!"
        >
          Leave a Review
        </ListMenuItem>
        {user && !user.IsGuestUser && (
          <div className={style.signOutContainer}>
            <Button
              className={style.signOutButton}
              variant="secondary"
              color="blue"
              text="Sign Out"
              showLeftIcon={false}
              showRightIcon={false}
              onClick={(event) => logoutCallback(event)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export type AccountMenuV2Props = OverlayMenuBaseProps & {
  user: UserState | undefined;
  loginURL: string;
  createAccountURL: string;
  isPdpMvpEnabled: boolean;
};

export const getAccountDisplayText = (
  nameData: Account.NameData | null | undefined,
  useLastNameInitial: boolean = false,
): string => {
  if (!nameData || nameData.firstName == null) {
    return "Account";
  }
  const prefix = (nameData.prefix ?? "").trim();
  const lastName = (nameData.lastName ?? "").trim();
  if ((prefix === "Dr." || prefix === "Prof.") && lastName) {
    const lastNameDisplay = useLastNameInitial ? lastName.charAt(0) : lastName;
    return `${prefix} ${lastNameDisplay}`;
  }
  return nameData.firstName.trim() || "Account";
};
